import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { GiOrganigram } from 'react-icons/gi'
import { TbBinaryTree } from 'react-icons/tb'

import { defineView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { DEPARTMENT_DELETE_CONFIRM_TEXT } from '../Contracts/IDepartment'
import { DepartmentFormTemplate } from '../Templates/DepartmentFormTemplate'

import type { DepartmentTreeTemplateProps } from '../Components/DepartmentTreeTemplate'
import type { DepartmentCardPerson } from '../Contracts/IDepartment'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'

// Lazy: полотно (xyflow + entitree-flex) вантажиться окремим шматком бандла
const DepartmentTree = lazy(() => import('../Components/DepartmentTreeTemplate'))

/** Мінімум headerPage-контексту, потрібний реєстрації варіантів списку. */
interface DepartmentHeaderPageContextLike {
    storages?: {
        crudContext?: {
            listContext?: {
                variant?: string | null
                variantItems?: Record<string, unknown>
                setVariant?: (variant: string | null) => void
            }
            setSetting?: (path: string, value: unknown) => void
        }
    }
}

interface FieldLike {
    mergeInputProps?: (props: Record<string, unknown>) => void
    setIsRequired?: (required: boolean) => void
}

interface DepFnArgs {
    field: FieldLike
    formContext: {
        contextKey?: string
        values?: Record<string, unknown>
        initialValues?: Record<string, unknown>
    }
}

const variantFallback = (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
    </div>
)

const LazyTreeTemplate = (props: DepartmentTreeTemplateProps) => (
    <Suspense fallback={variantFallback}>
        <DepartmentTree {...props} />
    </Suspense>
)

const isNewRecord = (values: Record<string, unknown> | undefined): boolean => {
    const uuid = values?.['uuid']

    return typeof uuid !== 'string' || uuid === ''
}

/**
 * «Підрозділ» — батьківський відділ (`SPEC-006` §7.4):
 *   • у головного поля немає (Р-04) — `false` з `depFn` ядро читає як «сховати»;
 *   • створення з вузла дерева — заповнене й вимкнене (батька вибрали «+»);
 *   • створення з таблиці — обов'язковий вибір (Р-03);
 *   • редагування — лише читання: переносять перетягуванням у дереві (Q5).
 */
const parentDepFn = ({ field, formContext }: DepFnArgs): false | void => {
    if (formContext.contextKey === 'filterFormContext') return

    const values = formContext.values ?? {}

    if (values['isMain'] === true) return false

    const isNew = isNewRecord(values)
    const presetFromTree = isNew && Boolean(formContext.initialValues?.['parent'])

    field.setIsRequired?.(isNew)
    field.mergeInputProps?.({ disabled: !isNew || presetFromTree })
}

/** Працівники в таблиці — лише активні, як у дереві (ВП-4). */
const activeUsersView = defineView((value) =>
    renderUsersView(
        (Array.isArray(value) ? value : []).filter(
            (person: DepartmentCardPerson | null) => person !== null && (person.status ?? 'active') === 'active'
        )
    )
)

/**
 * Структура компанії (`SPEC-006` §7): відділи як adjacency-дерево.
 *
 * Два представлення одних даних: «Дерево» — типове (полотно, перетягування,
 * окремий ендпоінт `/services/departments/tree`) і «Таблиця», яку ядро
 * додає кожному списку (ВП-6).
 *
 * Права НЕ дублюються тут: `accessFn` навмисно не заданий — працює
 * Ship-глобальний із `CrudResourceConfig` (колекційні права з
 * `/services/auth/permissions`, права запису — з `_permissions` бека).
 * Код, «Активний» і «Сортування» PD у формі не показуються (§12).
 */
export const DepartmentResource: IResourceConfig = {
    title: 'Структура компанії',
    fields: {
        title: {
            label: 'Назва',
            isRequired: true,
            inputProps: { maxLength: 150 },
        },
        parent: {
            label: 'Підрозділ',
            type: 'select',
            resource: 'Setting.Department',
            depFn: parentDepFn,
        },
        chief: {
            label: 'Керівник',
            type: 'select',
            isRequired: true,
            // Список співробітників ядро віддає лише з активними (`SPEC-005`)
            resource: 'Setting.User',
            // У перегляді й таблиці — людиною (аватар з ініціалами й ім'я), а не тегом
            view: renderUsersView,
        },
        // Склад відділу бек лише віддає (обернена M2M, Р-13): співробітника
        // до відділу додають у його картці, полем «Відділи компанії»
        users: {
            label: 'Співробітники',
            type: 'select',
            isMultiple: true,
            isEditable: false,
            resource: 'Setting.User',
            view: activeUsersView,
        },
    },
    table: {
        showColumns: ['title', 'parent', 'chief', 'users'],
    },
    filter: {
        includeFields: ['title'],
    },
    form: {
        template: DepartmentFormTemplate,
    },
    // Заголовок шухляди в усіх режимах — «Підрозділ», як у легасі (РК-06)
    detailConfig: {
        title: 'Підрозділ',
    },
    drawer: {
        width: 720,
    },
    /**
     * Варіант «Дерево» дописується у `variantItems` списку так само, як це
     * робить ядровий `AddTableViewModeToList` для таблиці: синхронно, у тому ж
     * ланцюгу `onAfterInit` — `variantItems` не observable, тож відкладена
     * зміна перемикач не перемалює. Дерево — типовий вигляд (§7.1).
     */
    headerPage: {
        eventHandlers: {
            onAfterInit: {
                AddDepartmentViewModes: ({
                    headerPageContext,
                }: {
                    headerPageContext?: DepartmentHeaderPageContextLike
                }): void => {
                    const crudContext = headerPageContext?.storages?.crudContext
                    const listContext = crudContext?.listContext
                    if (!crudContext || !listContext) return

                    listContext.variantItems = {
                        ...(listContext.variantItems ?? {}),
                        tree: {
                            label: 'Дерево',
                            icon: TbBinaryTree,
                            template: LazyTreeTemplate,
                            props: { crudContext },
                        },
                    }
                    listContext.setVariant?.('tree')

                    // Підтвердження видалення з меню рядка таблиці — з тим самим
                    // наслідком для людей, що й у дереві (Р-10)
                    crudContext.setSetting?.(
                        'AddDrawerModeCrudFeature.menuDeleteConfirmItemText',
                        DEPARTMENT_DELETE_CONFIRM_TEXT
                    )
                },
            },
        },
    },
    restUrl: '/services/departments',
    pageUrl: '/departments',
    pageConfig: {
        title: 'Структура компанії',
        icon: <GiOrganigram />,
    },
    settings: {
        searchFields: ['title'],
    },
}
